/**
 * map-bridge.ts
 *
 * Abstracts all map / GPS calls so that:
 *   - When running inside the ATAK plugin: delegates to the Java DataLab bridge.
 *   - When running in a normal browser: uses the Geolocation API for self-location
 *     and dispatches DOM events that the <WebMap> component listens to.
 */

export interface MarkerInfo {
  uid: string;
  name: string;
  lat: number;
  lon: number;
  remarks: string;
  triageLevel: string; // "T1" | "T2" | "T3" | "T4" | "T0" | ""
}

// ─── Singleton bridge interface ───────────────────────────────────────────────

export interface MapBridge {
  /** True when running inside the ATAK plugin. */
  isNative: boolean;
  /** Resolve current device position, or null if unavailable. */
  getSelfLocation(): Promise<{ lat: number; lon: number } | null>;
  /** Place or update a named marker on the map. */
  addMarker(info: MarkerInfo): void;
  /** Pan/zoom to the marker with the given uid. */
  focusMarker(uid: string): void;
  /** Remove all markers (e.g. on page unmount). */
  clearMarkers(): void;
}

// ─── ATAK (native) implementation ─────────────────────────────────────────────

/** Track UIDs of all markers placed via the ATAK bridge so we can remove them. */
const _atakPlacedUids = new Set<string>();

const atakBridge: MapBridge = {
  isNative: true,

  async getSelfLocation() {
    try {
      const raw = (window as any).DataLab?.getSelfLocation?.();
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.lat != null && parsed?.lon != null) return parsed;
      return null;
    } catch {
      return null;
    }
  },

  addMarker({ uid, name, lat, lon, remarks, triageLevel }) {
    _atakPlacedUids.add(uid);
    (window as any).DataLab?.addCotMarker?.(uid, name, lat, lon, remarks, triageLevel);
  },

  focusMarker(uid) {
    (window as any).DataLab?.focusCotMarker?.(uid);
  },

  clearMarkers() {
    for (const uid of _atakPlacedUids) {
      (window as any).DataLab?.removeCotMarker?.(uid);
    }
    _atakPlacedUids.clear();
  },
};

// ─── Web (browser) implementation ─────────────────────────────────────────────

/** Fires a custom DOM event for the WebMap component to handle. */
function dispatchMapEvent(name: string, detail: unknown) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

/**
 * Marker buffer — keeps the latest state of each marker so WebMap can replay
 * them when it mounts (markers dispatched before mount would otherwise be lost).
 */
const _markerBuffer = new Map<string, MarkerInfo>();

/** Called by WebMap on mount to render any markers added before it was ready. */
export function getBufferedMarkers(): MarkerInfo[] {
  return Array.from(_markerBuffer.values());
}

const webBridge: MapBridge = {
  isNative: false,

  getSelfLocation() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 5000, maximumAge: 30_000 },
      );
    });
  },

  addMarker(info) {
    _markerBuffer.set(info.uid, info);
    dispatchMapEvent("biostream:map-add-marker", info);
  },

  focusMarker(uid) {
    dispatchMapEvent("biostream:map-focus-marker", { uid });
  },

  clearMarkers() {
    _markerBuffer.clear();
    dispatchMapEvent("biostream:map-clear-markers", {});
  },
};

// ─── Exported singleton ────────────────────────────────────────────────────────
// Delegates lazily so BIOSTREAM_ENV is checked at call time, not at module init.
// (ATAK injects BIOSTREAM_ENV after JS modules load, so a one-time check would
// always see undefined and incorrectly pick the web implementation.)

function isAtak(): boolean {
  return !!(window as any).BIOSTREAM_ENV?.atak;
}

export const mapBridge: MapBridge = {
  get isNative() { return isAtak(); },
  getSelfLocation() { return isAtak() ? atakBridge.getSelfLocation() : webBridge.getSelfLocation(); },
  addMarker(info) { return isAtak() ? atakBridge.addMarker(info) : webBridge.addMarker(info); },
  focusMarker(uid) { return isAtak() ? atakBridge.focusMarker(uid) : webBridge.focusMarker(uid); },
  clearMarkers() { return isAtak() ? atakBridge.clearMarkers() : webBridge.clearMarkers(); },
};
